import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  Users, CheckCircle, Clock, RefreshCw, Trash2, 
  UserCheck, UserX, Search, CheckSquare, Layers, Download, FileText, FileSpreadsheet, User
} from "lucide-react";
import AdminSidebar from "../components/AdminSidebar";
import axios from "axios";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// User type labels for display
const USER_TYPE_LABELS = {
  "govt_secretariat": "Govt. Secretariat Staff",
  "govt_mts": "Govt. MTS Staff",
  "svep_mentor": "SVEP Mentor",
  "svep_crp_ep": "SVEP CRP-EPs",
  "svep_cep_coordinator": "SVEP CEP-EPs Coordinators",
  "staff_promotion": "Staff Promotion Exam",
  "vo_member": "VOs",
  "clf_member": "CLF",
  "shg_member": "SHGs",
  "other": "Others",
  // Legacy types for backwards compatibility
  "crp_ep": "CRP-EP",
  "mentor": "Mentor",
  "staff": "Staff"
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminCandidates() {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, candidate: null });
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [approveAllDialog, setApproveAllDialog] = useState(false);
  const [deleteAllDialog, setDeleteAllDialog] = useState(false);
  const [stats, setStats] = useState({ pending: 0, approved: 0, loggedIn: 0, submitted: 0 });
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [isRetrying, setIsRetrying] = useState(false);
  const [viewDialog, setViewDialog] = useState({ open: false, candidate: null });
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchCandidates();
  }, [navigate]);

  const fetchCandidates = async (retryCount = 0) => {
    setLoading(true);
    try {
      const [candidatesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/candidates`, { 
          params: { page: 1, limit: 500 },
          timeout: 30000 // 30 second timeout for large datasets
        }),
        axios.get(`${API}/admin/stats`, { timeout: 15000 })
      ]);
      
      // Handle both paginated and legacy response formats
      const candidatesData = candidatesRes.data.candidates || candidatesRes.data;
      setCandidates(Array.isArray(candidatesData) ? candidatesData : []);
      
      if (candidatesRes.data.total !== undefined) {
        setPagination({
          page: candidatesRes.data.page || 1,
          totalPages: candidatesRes.data.total_pages || 1,
          total: candidatesRes.data.total || 0
        });
      }
      
      const submittedCount = (Array.isArray(candidatesData) ? candidatesData : []).filter(c => c.has_submitted).length;
      setStats({
        pending: statsRes.data.pending_approval || 0,
        approved: statsRes.data.approved_candidates || 0,
        loggedIn: statsRes.data.logged_in_candidates || 0,
        submitted: submittedCount
      });
      setIsRetrying(false);
    } catch (error) {
      console.error("Failed to load candidates:", error);
      // Retry logic for network issues
      if (retryCount < 3) {
        setIsRetrying(true);
        toast.info(`Retrying... (${retryCount + 1}/3)`);
        setTimeout(() => fetchCandidates(retryCount + 1), 2000);
      } else {
        toast.error("Failed to load candidates. Please refresh the page.");
        setIsRetrying(false);
      }
    } finally {
      if (!isRetrying) setLoading(false);
    }
  };

  const handleApprove = async (candidateId) => {
    try {
      await axios.put(`${API}/admin/candidates/${candidateId}/approve`);
      toast.success("Candidate approved");
      fetchCandidates();
    } catch (error) {
      toast.error("Failed to approve candidate");
    }
  };

  const handleReject = async (candidateId) => {
    try {
      await axios.put(`${API}/admin/candidates/${candidateId}/reject`);
      toast.success("Approval revoked");
      fetchCandidates();
    } catch (error) {
      toast.error("Failed to revoke approval");
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) {
      toast.error("No candidates selected");
      return;
    }
    try {
      await axios.post(`${API}/admin/candidates/bulk-approve`, { candidate_ids: selectedIds });
      toast.success(`${selectedIds.length} candidates approved`);
      setSelectedIds([]);
      fetchCandidates();
    } catch (error) {
      toast.error("Failed to approve candidates");
    }
  };

  const handleApproveAll = async () => {
    try {
      const response = await axios.post(`${API}/admin/candidates/approve-all`);
      toast.success(`${response.data.approved_count} candidates approved`);
      setApproveAllDialog(false);
      fetchCandidates();
    } catch (error) {
      toast.error("Failed to approve all candidates");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error("No candidates selected");
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/candidates/bulk-delete`, { candidate_ids: selectedIds });
      toast.success(`${response.data.deleted_count} candidates deleted`);
      setSelectedIds([]);
      setBulkDeleteDialog(false);
      fetchCandidates();
    } catch (error) {
      toast.error("Failed to delete candidates");
    }
  };

  const handleDeleteAllPending = async () => {
    try {
      const response = await axios.post(`${API}/admin/candidates/delete-all-pending`);
      toast.success(`${response.data.deleted_count} pending candidates deleted`);
      setDeleteAllDialog(false);
      fetchCandidates();
    } catch (error) {
      toast.error("Failed to delete pending candidates");
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.candidate) return;
    try {
      await axios.delete(`${API}/admin/candidates/${deleteDialog.candidate.id}`);
      toast.success("Candidate deleted");
      setDeleteDialog({ open: false, candidate: null });
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete candidate");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllPending = () => {
    const pendingIds = filteredCandidates.filter(c => !c.approved).map(c => c.id);
    setSelectedIds(pendingIds);
  };

  const selectAllFiltered = () => {
    const allIds = filteredCandidates.map(c => c.id);
    setSelectedIds(allIds);
  };

  // Filter candidates based on active tab and search term
  const filteredCandidates = candidates.filter(c => {
    // First filter by tab
    let tabMatch = true;
    if (activeTab === "pending") tabMatch = !c.approved;
    else if (activeTab === "approved") tabMatch = c.approved && !c.has_submitted;
    else if (activeTab === "submitted") tabMatch = c.has_submitted;
    
    if (!tabMatch) return false;
    
    // Then filter by search term
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      c.name?.toLowerCase().includes(term) ||
      c.registration_number?.toLowerCase().includes(term) ||
      c.block?.toLowerCase().includes(term)
    );
  });

  // Download individual candidate record
  const downloadCandidateRecord = async (candidate, format = "excel") => {
    try {
      // Fetch full submission details if candidate has submitted
      let submission = null;
      if (candidate.has_submitted) {
        const response = await axios.get(`${API}/admin/download-all-records`);
        const allRecords = response.data.exam_records;
        submission = allRecords.find(r => r.candidate_info.registration_number === candidate.registration_number);
      }

      if (format === "excel") {
        const wb = XLSX.utils.book_new();
        
        // Candidate Info Sheet
        const candidateInfo = [{
          "Field": "Name", "Value": candidate.name
        }, {
          "Field": "Registration Number", "Value": candidate.registration_number
        }, {
          "Field": "User Type", "Value": USER_TYPE_LABELS[candidate.user_type] || candidate.user_type || "CRP-EP"
        }, {
          "Field": "State", "Value": candidate.state
        }, {
          "Field": "District", "Value": candidate.district
        }, {
          "Field": "Block", "Value": candidate.block
        }, {
          "Field": "Gram Panchayat", "Value": candidate.gram_panchayat || "-"
        }, {
          "Field": "Registered At", "Value": candidate.registered_at
        }, {
          "Field": "Approval Status", "Value": candidate.approved ? "Approved" : "Pending"
        }, {
          "Field": "Exam Status", "Value": candidate.has_submitted ? "Submitted" : "Not Submitted"
        }];
        
        if (submission) {
          candidateInfo.push(
            { "Field": "Section I Score", "Value": `${submission.scores.section1_score}/${submission.scores.section1_total}` },
            { "Field": "Section II Score", "Value": `${submission.scores.section2_score}/${submission.scores.section2_total}` },
            { "Field": "Section II Qualified", "Value": submission.scores.section2_qualified ? "Yes" : "No" },
            { "Field": "Section III Score", "Value": `${submission.scores.subjective_score}/${submission.scores.subjective_total}` },
            { "Field": "Total Score", "Value": `${submission.scores.total_score}/${submission.scores.max_score}` },
            { "Field": "Overall Result", "Value": submission.scores.qualified ? "QUALIFIED" : "NOT QUALIFIED" },
            { "Field": "Submitted At", "Value": submission.exam_info.submitted_at },
            { "Field": "Graded At", "Value": submission.exam_info.graded_at || "-" }
          );
        }
        
        const infoWs = XLSX.utils.json_to_sheet(candidateInfo);
        XLSX.utils.book_append_sheet(wb, infoWs, "Candidate Info");
        
        // Add answers if submitted
        if (submission && submission.mcq_answers) {
          const answersData = submission.mcq_answers.map((ans, idx) => ({
            "Q.No": idx + 1,
            "Question ID": ans.question_id,
            "Answer Given": ans.answer,
            "Is Correct": ans.is_correct ? "Yes" : "No"
          }));
          const answersWs = XLSX.utils.json_to_sheet(answersData);
          XLSX.utils.book_append_sheet(wb, answersWs, "MCQ Answers");
        }
        
        if (submission && submission.subjective_answers) {
          const subjData = submission.subjective_answers.map(ans => ({
            "Question ID": ans.question_id,
            "Answer": ans.answer
          }));
          const subjWs = XLSX.utils.json_to_sheet(subjData);
          XLSX.utils.book_append_sheet(wb, subjWs, "Subjective Answers");
        }
        
        XLSX.writeFile(wb, `${candidate.name.replace(/\s+/g, '_')}_${candidate.registration_number}.xlsx`);
        toast.success("Excel file downloaded!");
      } else {
        // PDF format with passport photo on right side
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        
        // Title
        doc.setFontSize(18);
        doc.setTextColor(0, 100, 0);
        doc.text("CRP-EP Examination Record", 14, 20);
        
        // Add passport photo on the right side if available
        if (candidate.photo) {
          try {
            // Photo positioned at top right (passport size: 35mm x 45mm approx)
            doc.addImage(candidate.photo, 'JPEG', pageWidth - 50, 15, 35, 45);
            // Add border around photo
            doc.setDrawColor(0, 128, 0);
            doc.setLineWidth(0.5);
            doc.rect(pageWidth - 50, 15, 35, 45);
          } catch (e) {
            console.error("Error adding photo to PDF:", e);
          }
        }
        
        // Candidate information (left side)
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text(`Candidate: ${candidate.name}`, 14, 35);
        doc.text(`Registration No: ${candidate.registration_number}`, 14, 42);
        doc.text(`User Type: ${USER_TYPE_LABELS[candidate.user_type] || candidate.user_type || "-"}`, 14, 49);
        doc.text(`Location: ${candidate.block}, ${candidate.district}, ${candidate.state}`, 14, 56);
        doc.text(`Gram Panchayat: ${candidate.gram_panchayat || "-"}`, 14, 63);
        
        if (submission) {
          doc.setFontSize(14);
          doc.text("Examination Results", 14, 80);
          
          const tableData = [
            ["Section I (GK/Eng/Reasoning)", `${submission.scores.section1_score}/${submission.scores.section1_total}`],
            ["Section II (Mathematics)", `${submission.scores.section2_score}/${submission.scores.section2_total}`],
            ["Section II Qualified", submission.scores.section2_qualified ? "Yes" : "No"],
            ["Section III (Subjective)", `${submission.scores.subjective_score}/${submission.scores.subjective_total}`],
            ["Total Score", `${submission.scores.total_score}/${submission.scores.max_score}`],
            ["Overall Result", submission.scores.qualified ? "QUALIFIED" : "NOT QUALIFIED"]
          ];
          
          autoTable(doc, {
            startY: 85,
            head: [["Section", "Score/Status"]],
            body: tableData,
            theme: "striped",
            headStyles: { fillColor: [0, 128, 0] }
          });
        } else {
          doc.setFontSize(12);
          doc.text("Exam Status: Not Submitted", 14, 80);
        }
        
        doc.save(`${candidate.name.replace(/\s+/g, '_')}_${candidate.registration_number}.pdf`);
        toast.success("PDF file downloaded!");
      }
    } catch (error) {
      toast.error("Failed to download record");
      console.error(error);
    }
  };

  // Bulk download PDFs for selected candidates
  const handleBulkDownloadPDF = async () => {
    if (selectedIds.length === 0) {
      toast.error("No candidates selected");
      return;
    }

    setBulkDownloading(true);
    setDownloadProgress(0);
    
    const selectedCandidates = candidates.filter(c => selectedIds.includes(c.id));
    
    toast.info(`Starting download of ${selectedCandidates.length} PDFs...`);
    
    for (let i = 0; i < selectedCandidates.length; i++) {
      const candidate = selectedCandidates[i];
      setDownloadProgress(i + 1);
      
      try {
        // Get submission data if available
        let submission = null;
        if (candidate.has_submitted) {
          const response = await axios.get(`${API}/admin/candidate/${candidate.id}/submission`);
          submission = response.data;
        }

        // Generate PDF
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        
        // Title
        doc.setFontSize(18);
        doc.setTextColor(0, 100, 0);
        doc.text("CRP-EP Examination Record", 14, 20);
        
        // Add passport photo if available
        if (candidate.photo) {
          try {
            doc.addImage(candidate.photo, 'JPEG', pageWidth - 50, 15, 35, 45);
            doc.setDrawColor(0, 128, 0);
            doc.setLineWidth(0.5);
            doc.rect(pageWidth - 50, 15, 35, 45);
          } catch (e) {
            console.error("Error adding photo:", e);
          }
        }
        
        // Candidate info
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text(`Candidate: ${candidate.name}`, 14, 35);
        doc.text(`Registration No: ${candidate.registration_number}`, 14, 42);
        doc.text(`User Type: ${USER_TYPE_LABELS[candidate.user_type] || candidate.user_type || "-"}`, 14, 49);
        doc.text(`Location: ${candidate.block}, ${candidate.district}, ${candidate.state}`, 14, 56);
        doc.text(`Gram Panchayat: ${candidate.gram_panchayat || "-"}`, 14, 63);
        
        if (submission) {
          doc.setFontSize(14);
          doc.text("Examination Results", 14, 80);
          
          const tableData = [
            ["Section I (GK/Eng/Reasoning)", `${submission.scores?.section1_score || 0}/${submission.scores?.section1_total || 50}`],
            ["Section II (Mathematics)", `${submission.scores?.section2_score || 0}/${submission.scores?.section2_total || 40}`],
            ["Section II Qualified", submission.scores?.section2_qualified ? "Yes" : "No"],
            ["Section III (Subjective)", `${submission.scores?.subjective_score || 0}/${submission.scores?.subjective_total || 10}`],
            ["Total Score", `${submission.scores?.total_score || 0}/${submission.scores?.max_score || 100}`],
            ["Overall Result", submission.scores?.qualified ? "QUALIFIED" : "NOT QUALIFIED"]
          ];
          
          autoTable(doc, {
            startY: 85,
            head: [["Section", "Score/Status"]],
            body: tableData,
            theme: "striped",
            headStyles: { fillColor: [0, 128, 0] }
          });
        } else {
          doc.setFontSize(12);
          doc.text("Exam Status: Not Submitted", 14, 80);
        }
        
        // Save with unique filename
        doc.save(`${candidate.name.replace(/\s+/g, '_')}_${candidate.registration_number}.pdf`);
        
        // Small delay between downloads to prevent browser blocking
        await new Promise(resolve => setTimeout(resolve, 300));
        
      } catch (error) {
        console.error(`Error downloading PDF for ${candidate.name}:`, error);
      }
    }
    
    setBulkDownloading(false);
    setDownloadProgress(0);
    toast.success(`Successfully downloaded ${selectedCandidates.length} PDFs!`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <AdminSidebar>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Candidate Management</h1>
            <p className="text-xs sm:text-sm text-gray-500">Approve candidates to take the exam</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchCandidates}
            data-testid="refresh-btn"
            className="self-start sm:self-auto"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card className="border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("pending")}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Pending Approval</CardTitle>
              <Clock className="w-5 h-5 text-amber-500" />
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900" data-testid="pending-count">
                {stats.pending}
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("approved")}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Approved</CardTitle>
              <CheckCircle className="w-5 h-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900" data-testid="approved-count">
                {stats.approved}
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("submitted")}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Exam Submitted</CardTitle>
              <FileText className="w-5 h-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900" data-testid="submitted-count">
                {stats.submitted}
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("all")}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Candidates</CardTitle>
              <Users className="w-5 h-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900" data-testid="total-count">
                {candidates.length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
            <TabsTrigger value="all">All ({candidates.length})</TabsTrigger>
            <TabsTrigger value="pending">Pending ({stats.pending})</TabsTrigger>
            <TabsTrigger value="approved">Approved ({stats.approved - stats.submitted})</TabsTrigger>
            <TabsTrigger value="submitted" className="text-purple-700">Submitted ({stats.submitted})</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name, registration number, or block..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedIds(filteredCandidates.map(c => c.id))}
              data-testid="select-all-btn"
            >
              <CheckSquare className="w-4 h-4 mr-2" /> Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={selectAllPending}
              data-testid="select-all-pending-btn"
            >
              <Clock className="w-4 h-4 mr-2" /> Select Pending
            </Button>
            <Button
              size="sm"
              onClick={handleBulkApprove}
              disabled={selectedIds.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="bulk-approve-btn"
            >
              <UserCheck className="w-4 h-4 mr-2" /> Approve ({selectedIds.length})
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBulkDeleteDialog(true)}
              disabled={selectedIds.length === 0}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete ({selectedIds.length})
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleBulkDownloadPDF}
              disabled={selectedIds.length === 0 || bulkDownloading}
              className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
              data-testid="bulk-download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" /> 
              {bulkDownloading ? `Downloading... (${downloadProgress}/${selectedIds.length})` : `Download PDFs (${selectedIds.length})`}
            </Button>
          </div>
        </div>

        {/* Bulk Actions Row */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setApproveAllDialog(true)}
            className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            data-testid="approve-all-btn"
          >
            <CheckCircle className="w-4 h-4 mr-2" /> Approve All Pending
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteAllDialog(true)}
            className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
            data-testid="delete-all-pending-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete All Pending
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/question-sets")}
            className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
            data-testid="question-sets-btn"
          >
            <Layers className="w-4 h-4 mr-2" /> Question Sets
          </Button>
        </div>

        {/* Candidates Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Registered Candidates
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : filteredCandidates.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No candidates found</p>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox 
                          checked={selectedIds.length === filteredCandidates.length && selectedIds.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) selectAllFiltered();
                            else setSelectedIds([]);
                          }}
                          data-testid="select-all-header-checkbox"
                        />
                      </TableHead>
                      <TableHead>Candidate Name</TableHead>
                      <TableHead>Reg. Number</TableHead>
                      <TableHead>User Type</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Gram Panchayat</TableHead>
                      <TableHead>Registered At</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCandidates.map((candidate) => (
                      <TableRow 
                        key={candidate.id} 
                        className={candidate.logged_in ? "bg-blue-50/50" : ""}
                        data-testid={`candidate-row-${candidate.id}`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(candidate.id)}
                            onCheckedChange={() => toggleSelect(candidate.id)}
                            data-testid={`select-${candidate.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setViewDialog({ open: true, candidate })}
                              className="text-emerald-700 hover:text-emerald-900 hover:underline cursor-pointer font-medium text-left"
                              data-testid={`view-candidate-${candidate.id}`}
                            >
                              {candidate.name}
                            </button>
                            {candidate.logged_in && (
                              <Badge variant="outline" className="bg-blue-100 text-blue-700 text-xs">
                                Online
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{candidate.registration_number}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs bg-slate-50">
                            {USER_TYPE_LABELS[candidate.user_type] || candidate.user_type || "CRP-EP"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-gray-600">
                          <div>{candidate.block}</div>
                          <div className="text-gray-400">{candidate.district}, {candidate.state}</div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {candidate.gram_panchayat || "-"}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {formatDate(candidate.registered_at)}
                        </TableCell>
                        <TableCell>
                          {candidate.has_submitted ? (
                            <Badge className="bg-purple-100 text-purple-800">Submitted</Badge>
                          ) : candidate.approved ? (
                            <Badge className="bg-green-100 text-green-800">Approved</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Download buttons */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadCandidateRecord(candidate, "excel")}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Download Excel"
                              data-testid={`download-excel-${candidate.id}`}
                            >
                              <FileSpreadsheet className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadCandidateRecord(candidate, "pdf")}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Download PDF"
                              data-testid={`download-pdf-${candidate.id}`}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            {!candidate.approved ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleApprove(candidate.id)}
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                data-testid={`approve-${candidate.id}`}
                              >
                                <UserCheck className="w-4 h-4" />
                              </Button>
                            ) : !candidate.has_submitted && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReject(candidate.id)}
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                data-testid={`revoke-${candidate.id}`}
                              >
                                <UserX className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteDialog({ open: true, candidate })}
                              data-testid={`delete-${candidate.id}`}
                              title="Delete candidate"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Candidate?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteDialog.candidate?.name}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialog} onOpenChange={setBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Candidates?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedIds.length}</strong> selected candidates? 
              This will also delete their exam submissions if any.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-bulk-delete-btn"
            >
              Delete Selected
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approve All Confirmation Dialog */}
      <AlertDialog open={approveAllDialog} onOpenChange={setApproveAllDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve All Pending Candidates?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to approve <strong>all {stats.pending}</strong> pending candidates?
              They will be able to take the exam immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleApproveAll}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-approve-all-btn"
            >
              Approve All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Pending Confirmation Dialog */}
      <AlertDialog open={deleteAllDialog} onOpenChange={setDeleteAllDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete All Pending Candidates?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>all pending</strong> candidates who haven't submitted exams?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteAllPending}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-all-btn"
            >
              Delete All Pending
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Candidate Details Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => setViewDialog({ ...viewDialog, open })}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-600" />
              Candidate Details
            </DialogTitle>
          </DialogHeader>
          {viewDialog.candidate && (
            <div className="py-4">
              <div className="flex gap-6">
                {/* Photo Section */}
                <div className="flex-shrink-0">
                  {viewDialog.candidate.photo ? (
                    <img 
                      src={viewDialog.candidate.photo} 
                      alt="Passport Photo" 
                      className="w-28 h-36 object-cover rounded-lg border-2 border-emerald-200"
                      data-testid="candidate-photo"
                    />
                  ) : (
                    <div className="w-28 h-36 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                      <User className="w-10 h-10 text-gray-400" />
                    </div>
                  )}
                </div>
                
                {/* Info Section */}
                <div className="flex-1 space-y-3">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{viewDialog.candidate.name}</h3>
                    <p className="text-sm text-gray-500">{viewDialog.candidate.registration_number}</p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-slate-50">
                      {USER_TYPE_LABELS[viewDialog.candidate.user_type] || viewDialog.candidate.user_type || "CRP-EP"}
                    </Badge>
                    {viewDialog.candidate.approved ? (
                      viewDialog.candidate.has_submitted ? (
                        <Badge className="bg-blue-100 text-blue-700">Submitted</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700">Approved</Badge>
                      )
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Details Grid */}
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-1">
                  <p className="text-gray-500 text-xs uppercase">Mobile Number</p>
                  <p className="font-medium">{viewDialog.candidate.mobile_number || "-"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-gray-500 text-xs uppercase">State</p>
                  <p className="font-medium">{viewDialog.candidate.state || "-"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-gray-500 text-xs uppercase">District</p>
                  <p className="font-medium">{viewDialog.candidate.district || "-"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-gray-500 text-xs uppercase">Block</p>
                  <p className="font-medium">{viewDialog.candidate.block || "-"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-gray-500 text-xs uppercase">Gram Panchayat</p>
                  <p className="font-medium">{viewDialog.candidate.gram_panchayat || "-"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-gray-500 text-xs uppercase">Registered At</p>
                  <p className="font-medium">
                    {viewDialog.candidate.registered_at 
                      ? new Date(viewDialog.candidate.registered_at).toLocaleString() 
                      : "-"}
                  </p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="mt-6 flex gap-2 justify-end border-t pt-4">
                {!viewDialog.candidate.approved && (
                  <Button
                    size="sm"
                    onClick={() => {
                      handleApprove(viewDialog.candidate.id);
                      setViewDialog({ open: false, candidate: null });
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <UserCheck className="w-4 h-4 mr-2" /> Approve
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const c = viewDialog.candidate;
                    handleDownload(c, 'pdf');
                    setViewDialog({ open: false, candidate: null });
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </AdminSidebar>
  );
}
